import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';
import { allTenantDependentDataCleared } from '../../action/authenticationEvents';
import { Group, Location } from '../../api/types';
// eslint-disable-next-line max-len
import { AssetGroupWithAvailabilities, CreateBookingNonRoomRentalState, PersonalDetails, PersonalDetailsValidation } from '../../types/bookingCreationNonRoomRental';

// Action Payload Interfaces
export interface SetUseCasePayload {
    useCase: string;
}

export interface SetStartDatePayload {
    startDate: Dayjs | null;
}

export interface SetEndDatePayload {
    endDate: Dayjs | null;
}

export interface SetStartTimePayload {
    startTime: string;
}

export interface SetDurationPayload {
    duration: number; // in minutes
}

export interface SetLocationPayload {
    location: Location | null;
}

export interface SetAmountPayload {
    amount: number;
}

export interface SetIsFullDayPayload {
    isFullDay: boolean;
}

export interface SetPersonalDetailPayload {
    field: keyof PersonalDetails;
    value: string;
}

export interface SetCommentPayload {
    comment: string;
}

export interface SetEmailNotePayload {
    emailNote: string;
}

export interface SetSubmittingPayload {
    isSubmitting: boolean;
}

export interface SetDiscountPayload {
    discount: number;
}

export interface SetQuantityPayload {
    quantity: number;
}

// Validation Helper
const validatePersonalDetails = (details: PersonalDetails): PersonalDetailsValidation => {
    return {
        firstName: details.firstName.trim().length > 0,
        lastName: details.lastName.trim().length > 0,
        email: details.email.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email),
        phone: details.phone.trim().length >= 10,
    };
};

const isFormValid = (state: CreateBookingNonRoomRentalState): boolean => {
    const { reservationDetails, personalDetailsValidation } = state;

    return !!(
        reservationDetails.startDate &&
        reservationDetails.endDate &&
        reservationDetails.startTime &&
        reservationDetails.duration > 0 &&
        reservationDetails.selectedLocation &&
        reservationDetails.amount > 0 &&
        personalDetailsValidation.firstName &&
        personalDetailsValidation.lastName &&
        personalDetailsValidation.email
    );
};

const initialState: CreateBookingNonRoomRentalState = {
    useCase: '',
    assetGroups: [],
    selectedGroup: null,
    assetGroupWithAvailabilities: null,
    reservationDetails: {
        startDate: null,
        endDate: null,
        startTime: '',
        duration: 0,
        selectedLocation: null,
        amount: 1,
        comment: '',
        emailNote: '',
        isFullDay: false,
        discount: 0,
        quantity: 1,
    },
    personalDetails: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    },
    personalDetailsValidation: {
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
    },
    isSubmitting: false,
    isFormValid: false,
};

const bookingCreationNonRoomRentalSlice = createSlice({
    name: 'createBookingNonRoomRental',
    initialState,
    reducers: {
        setUseCase: (state, action: PayloadAction<SetUseCasePayload>) => {
            state.useCase = action.payload.useCase;
        },

        setStartDate: (state, action: PayloadAction<SetStartDatePayload>) => {
            state.reservationDetails.startDate = action.payload.startDate;
            state.isFormValid = isFormValid(state);
        },

        setEndDate: (state, action: PayloadAction<SetEndDatePayload>) => {
            state.reservationDetails.endDate = action.payload.endDate;
            state.isFormValid = isFormValid(state);
        },

        setStartTime: (state, action: PayloadAction<SetStartTimePayload>) => {
            state.reservationDetails.startTime = action.payload.startTime;
            state.isFormValid = isFormValid(state);
        },

        setDuration: (state, action: PayloadAction<SetDurationPayload>) => {
            state.reservationDetails.duration = action.payload.duration;
            state.isFormValid = isFormValid(state);
        },

        setSelectedLocation: (state, action: PayloadAction<SetLocationPayload>) => {
            state.reservationDetails.selectedLocation = action.payload.location;
            state.isFormValid = isFormValid(state);
        },

        setAmount: (state, action: PayloadAction<SetAmountPayload>) => {
            state.reservationDetails.amount = action.payload.amount;
            state.isFormValid = isFormValid(state);
        },

        setIsFullDay: (state, action: PayloadAction<SetIsFullDayPayload>) => {
            state.reservationDetails.isFullDay = action.payload.isFullDay;
            // When switching to full day, clear time-based fields
            if (action.payload.isFullDay) {
                state.reservationDetails.startTime = '';
                state.reservationDetails.duration = 0;
                state.reservationDetails.endDate = null;
            }
            state.isFormValid = isFormValid(state);
        },

        setPersonalDetail: (state, action: PayloadAction<SetPersonalDetailPayload>) => {
            const { field, value } = action.payload;
            state.personalDetails[field] = value;
            state.personalDetailsValidation = validatePersonalDetails(state.personalDetails);
            state.isFormValid = isFormValid(state);
        },

        setComment: (state, action: PayloadAction<SetCommentPayload>) => {
            state.reservationDetails.comment = action.payload.comment;
        },

        setEmailNote: (state, action: PayloadAction<SetEmailNotePayload>) => {
            state.reservationDetails.emailNote = action.payload.emailNote;
        },

        setSubmitting: (state, action: PayloadAction<SetSubmittingPayload>) => {
            state.isSubmitting = action.payload.isSubmitting;
        },

        setDiscount: (state, action: PayloadAction<SetDiscountPayload>) => {
            state.reservationDetails.discount = action.payload.discount;
        },

        setQuantity: (state, action: PayloadAction<SetQuantityPayload>) => {
            state.reservationDetails.quantity = action.payload.quantity;
            state.isFormValid = isFormValid(state);
        },

        setAssetGroups: (state, action: PayloadAction<{ groups: Group[] }>) => {
            state.assetGroups = action.payload.groups;
        },

        setSelectedAssetGroup: (state, action: PayloadAction<{ group: Group | null }>) => {
            state.selectedGroup = action.payload.group;
            state.assetGroupWithAvailabilities = null;
        },

        setAssetGroupWithAvailabilities: (
            state,
            action: PayloadAction<{
                data: AssetGroupWithAvailabilities | null;
            }>,
        ) => {
            state.assetGroupWithAvailabilities = action.payload.data;
        },

        resetReservationData: (state) => {
            const currentLocation = state.reservationDetails.selectedLocation;
            const comment = state.reservationDetails.comment;
            const emailNote = state.reservationDetails.emailNote;
            const discount = state.reservationDetails.discount;
            state.reservationDetails = {
                startDate: null,
                endDate: null,
                startTime: '',
                duration: 0,
                selectedLocation: currentLocation,
                amount: 1,
                comment: comment,
                emailNote: emailNote,
                isFullDay: false,
                discount: discount,
                quantity: 1,
            };
            state.selectedGroup = null;
            state.assetGroupWithAvailabilities = null;
            state.isFormValid = false;
        },

        clearBookingForm: (state) => {
            return initialState;
        },
    },
    // Wipe any in-flight Create-Booking form state when the tenant changes,
    // so a partially-filled draft from tenant A (selected location, dates,
    // customer info) can't bleed into tenant B's form.
    extraReducers: (builder) => {
        builder.addCase(allTenantDependentDataCleared.toString(), () => initialState);
    },
});

export const {
    setUseCase,
    setStartDate,
    setEndDate,
    setStartTime,
    setDuration,
    setSelectedLocation,
    setAmount,
    setIsFullDay,
    setPersonalDetail,
    setComment,
    setEmailNote,
    setSubmitting,
    setDiscount,
    setQuantity,
    setAssetGroups,
    setSelectedAssetGroup,
    setAssetGroupWithAvailabilities,
    resetReservationData,
    clearBookingForm,
} = bookingCreationNonRoomRentalSlice.actions;

export { bookingCreationNonRoomRentalSlice };
